import type { CSSProperties } from 'react'

import type { CalendarBlockSource, CalendarLane } from '@app/shared'

// ============================================================================
// THE SOURCE PALETTE, as class names — docs/design/calendar-brief.md → Source palette.
//
// One table, one place. Every tint, rule, title colour and secondary colour on this screen is
// looked up here; nothing picks its own. That matters more than it usually does, because the
// brief calls the secondary colours a CONTRAST FLOOR ("~3.5:1 on their tint — do not lighten
// them, including the 8px provenance labels"), and a floor that is restated per component is a
// floor somebody lowers by accident.
//
// FIVE ROWS, FOUR SOURCES. `due` is not a block source — it is the tone the all-day row wears
// when a deadline is still open and already here (see `dueTone` in @app/shared). It lives in the
// same table because it is one row of the brief's one table.
// ============================================================================

export interface PaletteRow {
  /** The block's soft ground. */
  tint: string
  /** The 2.5px (week) / 3px (day) left rule. */
  rule: string
  /** Title text. */
  ink: string
  /** Time line, location, and the provenance stamp. The contrast floor. */
  ink2: string
  /** The 11px swatch on the sources list, and the 7px dot in the day summary. */
  dot: string
  /**
   * THE SAME TWO COLOURS AS CSS VALUES, for the motion system.
   *
   * `.mo-ink` / `.mo-rule` (index.css → THE MOTION SYSTEM) take ONE per-instance knob —
   * `--mo-rule`, the SOURCE hue — and derive the hover fill from it. A class name cannot be
   * handed to a custom property, so the same two tokens are restated here as `var(…)` values.
   * They are the identical tokens the classes above resolve to, which is why they live in this
   * row rather than in a second table beside it: a lane whose hover hue could drift from its
   * resting rule is exactly the drift this file exists to prevent, and dark mode follows for
   * free because every one of these tokens is re-declared under [data-theme='dark'].
   */
  hue: string
  /**
   * The block's RESTING ground, as a CSS value — what `--mo-tint` deepens FROM on hover.
   *
   * Without it the system's default (`--mo-rule` at 6% over whatever is behind) would REPLACE a
   * block's tint with a paler wash, so hovering a Canvas class would make it lighter. A block
   * already wears its lane's colour at rest; the hover has to add ink to that, not swap it.
   */
  ground: string
}

export type PaletteKey = CalendarBlockSource | 'due'

export const PALETTE: Record<PaletteKey, PaletteRow> = {
  canvas: {
    tint: 'bg-cal-canvas-tint',
    rule: 'border-l-cal-canvas-rule',
    ink: 'text-cal-canvas-ink',
    ink2: 'text-cal-canvas-ink-2',
    dot: 'bg-cal-canvas-rule',
    hue: 'var(--color-cal-canvas-rule)',
    ground: 'var(--color-cal-canvas-tint)',
  },
  personal: {
    tint: 'bg-cal-personal-tint',
    rule: 'border-l-cal-personal-rule',
    ink: 'text-cal-personal-ink',
    ink2: 'text-cal-personal-ink-2',
    dot: 'bg-cal-personal-rule',
    hue: 'var(--color-cal-personal-rule)',
    ground: 'var(--color-cal-personal-tint)',
  },
  work: {
    tint: 'bg-cal-work-tint',
    rule: 'border-l-cal-work-rule',
    ink: 'text-cal-work-ink',
    ink2: 'text-cal-work-ink-2',
    dot: 'bg-cal-work-rule',
    hue: 'var(--color-cal-work-rule)',
    ground: 'var(--color-cal-work-tint)',
  },
  learning: {
    tint: 'bg-cal-learning-tint',
    rule: 'border-l-cal-learning-rule',
    ink: 'text-cal-learning-ink',
    ink2: 'text-cal-learning-ink-2',
    dot: 'bg-cal-learning-rule',
    hue: 'var(--color-cal-learning-rule)',
    ground: 'var(--color-cal-learning-tint)',
  },
  // A task-linked block wears the learning violet, because that is what the brief's table calls
  // the row: "Learning / suggested". A block held for a task IS focused work.
  task: {
    tint: 'bg-cal-learning-tint',
    rule: 'border-l-cal-learning-rule',
    ink: 'text-cal-learning-ink',
    ink2: 'text-cal-learning-ink-2',
    dot: 'bg-cal-learning-rule',
    hue: 'var(--color-cal-learning-rule)',
    ground: 'var(--color-cal-learning-tint)',
  },
  // THE SIXTH ROW, AND IT IS NOT IN THE BRIEF'S TABLE. The brief drew five sources and a Google
  // row that was switched off, because no connector existed; one does now, so its events need a
  // lane of their own. Deliberately the only unsaturated row in the table — see the rationale
  // block beside the tokens at the end of src/index.css. Reusing Canvas blue would say a Google
  // event came from a course; reusing Personal amber would file time the owner never filed.
  google: {
    tint: 'bg-cal-google-tint',
    rule: 'border-l-cal-google-rule',
    ink: 'text-cal-google-ink',
    ink2: 'text-cal-google-ink-2',
    dot: 'bg-cal-google-rule',
    hue: 'var(--color-cal-google-rule)',
    ground: 'var(--color-cal-google-tint)',
  },
  due: {
    tint: 'bg-cal-due-tint',
    rule: 'border-l-cal-due-rule',
    ink: 'text-cal-due-ink',
    ink2: 'text-cal-due-ink-2',
    dot: 'bg-cal-due-rule',
    hue: 'var(--color-cal-due-rule)',
    ground: 'var(--color-cal-due-tint)',
  },
}

// ── The motion system's per-instance knobs, derived from the row above ──────
//
// 12d IS CHOSEN BY BORDER-RADIUS, NOT BY WHAT THE THING IS (index.css → THE MOTION SYSTEM), so
// this screen uses both mechanics and neither is a taste call:
//
//   · a grid block and an all-day pill are radius 6–8 surfaces  → `.mo-rule`  (`blockMotion`)
//   · a day-header due pill is radius 14, fully rounded         → `.mo-ink`   (`pillMotion`)
//
// Both take the SAME one knob — `--mo-rule`, the lane's own hue — so a hover can never light up
// in a colour the block is not already wearing.

/**
 * `.mo-ink`'s knob: just the hue. The class derives a 10% translucent wash from it and paints
 * that OVER whatever ground the pill already has, which is exactly right on a tinted pill.
 */
export function pillMotion(row: PaletteRow): CSSProperties {
  return { '--mo-rule': row.hue } as CSSProperties
}

/**
 * `.mo-rule`'s knobs: the hue, and an explicit `--mo-tint` that DEEPENS the block's resting
 * ground instead of replacing it.
 *
 * The class's own default is 6% of the hue over transparent — a fine wash for a white task row,
 * and wrong here: `.mo-rule:hover` sets `background-color`, which is unlayered and therefore
 * beats the `bg-cal-*-tint` utility the block wears at rest. Left at the default, hovering a
 * Canvas class would make it LIGHTER. Mixing the hue into the block's own tint instead means
 * hover adds ink to the lane's colour, which is what §1's "hover says where something came
 * from" asks for. The mix is the class's own idiom, and both operands are tokens, so dark mode
 * re-resolves for free.
 */
export function blockMotion(row: PaletteRow): CSSProperties {
  return {
    '--mo-rule': row.hue,
    '--mo-tint': `color-mix(in srgb, ${row.hue} 14%, ${row.ground})`,
  } as CSSProperties
}

/** The lane dots in "Where today goes" — the same hues, keyed by lane. */
export const LANE_DOT: Record<CalendarLane, string> = {
  class: 'bg-cal-canvas-rule',
  focus: 'bg-cal-learning-rule',
  work: 'bg-cal-work-rule',
  personal: 'bg-cal-personal-rule',
  google: 'bg-cal-google-rule',
}

/** The same hues, as bar fills. Separate from the dots so a retune of one cannot move the other. */
export const LANE_BAR: Record<CalendarLane, string> = {
  class: 'bg-cal-canvas-rule',
  focus: 'bg-cal-learning-rule',
  work: 'bg-cal-work-rule',
  personal: 'bg-cal-personal-rule',
  google: 'bg-cal-google-rule',
}
